import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import { isDepositItem, type DepositConfig } from "./deposit";

/** Pack contents by parent line number. */
export type PackContents = Record<number, BasketItem[]>;

/**
 * `ChildTypeId` is missing from the `norce-pwa` type but present in the
 * payload: a structure's own child lines come back as `1`, parents as `null`.
 */
type BasketLine = BasketItem & { ChildTypeId?: number | null };
const STRUCTURE_CHILD = 1;

/**
 * The structure lines Norce hangs off a pack, kept aside so the cart can show
 * what a pack holds — `filterBasketItemsForDisplay` strips them from
 * `basket.Items`.
 *
 * Structure children only. The `ChildTypeId` guard is not what hides a
 * promotion gift (the filter does that) — it is what keeps the gift's parent
 * its quantity stepper, which a line with contents loses because Norce rebuilds
 * a structure's children on every quantity change. Deposit fees are out, they
 * have their own summary row.
 */
export const getPackContents = (
	items: BasketItem[] | undefined | null,
	depositConfig?: DepositConfig
): PackContents => {
	const contents: PackContents = {};
	if (!items?.length) return contents;

	const lineNos = new Set(items.map((item) => item.LineNo));

	for (const item of items as BasketLine[]) {
		const parentLineNo = item.ParentLineNo;
		if (parentLineNo == null || !lineNos.has(parentLineNo)) continue;
		if (item.ChildTypeId !== STRUCTURE_CHILD) continue;
		if (isDepositItem(item, depositConfig)) continue;

		contents[parentLineNo] = [...(contents[parentLineNo] ?? []), item];
	}

	return contents;
};
